import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { products, reviews } from '../data/mockData';
import ProductCard from '../components/ProductCard';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiArrowRight, FiStar } = FiIcons;

export default function Home({ onAddToCart }) {
  const featured = products.slice(0, 4);

  return (
    <main className="overflow-x-hidden">
      {/* Hero Section - Above the Fold */}
      <section className="relative h-[90vh] md:h-screen flex items-center justify-center overflow-hidden bg-smoke">
        <motion.div 
          initial={{ scale: 1.05, opacity: 0 }}
          animate={{ scale: 1, opacity: 0.6 }}
          transition={{ duration: 1.8, ease: "easeOut" }}
          className="absolute inset-0 z-0"
        >
          <picture>
            <source 
              srcSet="https://images.unsplash.com/photo-1612196808214-b7e239e5f6b7?auto=format,compress&fit=crop&q=80&w=1600&fm=webp 1600w, https://images.unsplash.com/photo-1612196808214-b7e239e5f6b7?auto=format,compress&fit=crop&q=80&w=1200&fm=webp 1200w" 
              type="image/webp" 
            />
            <img 
              src="https://images.unsplash.com/photo-1612196808214-b7e239e5f6b7?auto=format,compress&fit=crop&q=80&w=1600" 
              alt="" 
              fetchpriority="high"
              width="1600"
              height="900"
              className="w-full h-full object-cover object-center"
            />
          </picture>
          <div className="absolute inset-0 bg-gradient-to-b from-smoke/30 via-smoke/50 to-smoke"></div>
        </motion.div>
        
        <div className="relative z-10 text-center max-w-5xl px-6 flex flex-col items-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.8 }}
            className="hidden lg:block absolute -right-20 top-1/2 -translate-y-1/2 w-64 h-80 border border-white/10 p-2 bg-smoke/40 backdrop-blur-md shadow-2xl"
          >
            <picture>
              <source srcSet="https://images.unsplash.com/photo-1601362840469-51e4d8d59085?auto=format,compress&fit=crop&q=80&w=600&fm=webp" type="image/webp" />
              <img 
                src="https://images.unsplash.com/photo-1601362840469-51e4d8d59085?auto=format,compress&fit=crop&q=80&w=600" 
                alt="Close-up of hand-poured ceramic vessel with textured finish" 
                width="240"
                height="300"
                className="w-full h-full object-cover grayscale opacity-80"
                loading="eager"
                decoding="async"
              />
            </picture>
            <div className="absolute bottom-4 left-4 right-4 text-left">
              <span className="text-[9px] uppercase tracking-[0.3em] text-linen/40 font-bold">Vessel Detail No. 04</span>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, letterSpacing: "0.5em" }}
            animate={{ opacity: 1, letterSpacing: "0.3em" }}
            transition={{ duration: 1.2 }}
            className="text-ember uppercase text-[11px] font-bold mb-8 tracking-[0.4em]"
          >
            Refined Home Fragrance
          </motion.div>
          
          <motion.h1 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.3 }}
            className="font-serif text-6xl md:text-8xl lg:text-9xl text-linen mb-10 leading-[0.9] tracking-tight"
          >
            Curate Your <br/><span className="italic font-normal opacity-90">Atmosphere</span>
          </motion.h1>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-6"
          >
            <Link to="/shop" className="group flex items-center space-x-4 bg-linen text-smoke px-10 py-5 uppercase tracking-[0.2em] text-[11px] font-bold hover:bg-ember hover:text-linen transition-all duration-500 shadow-xl">
              <span>Explore Collection</span>
              <SafeIcon icon={FiArrowRight} className="group-hover:translate-x-2 transition-transform" />
            </Link>
            <Link to="/about" className="text-linen/60 hover:text-linen transition-colors uppercase tracking-[0.2em] text-[11px] font-bold border-b border-white/20 hover:border-linen pb-1">
              Our Process
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Featured Section - Below the Fold */}
      <section className="py-32 px-6 md:px-12 max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row justify-between items-end mb-20">
          <div className="max-w-xl">
            <h2 className="font-serif text-4xl md:text-5xl text-linen mb-6 leading-tight">Hand-Poured in the <span className="italic">Shadows</span></h2>
            <p className="text-linen/50 text-lg leading-relaxed">Our most coveted fragrances, meticulously crafted in small batches to transform your living space into a sensory sanctuary.</p>
          </div>
          <Link to="/shop" className="mt-8 md:mt-0 text-ember hover:text-linen transition-colors uppercase tracking-[0.2em] text-[10px] font-bold flex items-center space-x-2 border-b border-ember/30 hover:border-linen pb-2">
            <span>View Full Library</span>
            <SafeIcon icon={FiArrowRight} />
          </Link>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
          {featured.map(product => (
            <ProductCard key={product.id} product={product} onAddToCart={() => onAddToCart(product)} />
          ))}
        </div>
      </section>

      {/* Scent Discovery Grid */}
      <section className="py-32 bg-smoke-light relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-20">
            <span className="text-ember font-bold text-[10px] tracking-[0.3em] uppercase block mb-4">Fragrance Families</span>
            <h2 className="font-serif text-4xl md:text-5xl text-linen">Find Your Signature</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { name: 'Woodsy', img: 'https://images.unsplash.com/photo-1441974231531-c6227db76b6e?auto=format&fit=crop&q=80&w=400', alt: 'Dense forest canopy reflecting woodsy notes' },
              { name: 'Earthy', img: 'https://images.unsplash.com/photo-1518531933037-91b2f5f229cc?auto=format&fit=crop&q=80&w=400', alt: 'Damp forest floor moss and roots' },
              { name: 'Sweet', img: 'https://images.unsplash.com/photo-1541339907198-e08756eaaaf8?auto=format&fit=crop&q=80&w=400', alt: 'Warm vanilla pods and spices' },
              { name: 'Citrus', img: 'https://images.unsplash.com/photo-1557800636-894a64c1696f?auto=format&fit=crop&q=80&w=400', alt: 'Zesty bergamot and bright botanicals' }
            ].map((cat, i) => (
              <Link to={`/shop?family=${cat.name}`} key={cat.name} className="group relative aspect-[4/5] overflow-hidden">
                <img 
                  src={cat.img} 
                  alt={cat.alt}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover grayscale opacity-40 group-hover:grayscale-0 group-hover:opacity-80 transition-all duration-1000 group-hover:scale-105" 
                />
                <div className="absolute inset-0 bg-smoke/40 group-hover:bg-smoke/10 transition-colors duration-500"></div>
                <div className="absolute inset-0 p-8 flex flex-col justify-between items-center text-center">
                  <span className="text-white/20 font-serif text-5xl group-hover:text-ember/40 transition-colors duration-500">0{i+1}</span>
                  <div className="mb-4">
                    <h3 className="font-serif text-2xl text-linen mb-2">{cat.name}</h3>
                    <span className="text-[10px] uppercase tracking-[0.2em] text-linen/40 group-hover:text-linen transition-colors">Explore Library</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Bedside Lighting Scene - Contextual Use Case */}
      <section className="py-40 bg-smoke relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-20 items-center">
          <div className="relative order-2 lg:order-1">
            <div className="aspect-[4/5] border border-white/10 p-3 bg-smoke-light">
              <picture>
                <source srcSet="https://images.unsplash.com/photo-1620189507195-68309c04c4d0?auto=format,compress&fit=crop&q=80&w=800&fm=webp" type="image/webp" />
                <img 
                  src="https://images.unsplash.com/photo-1620189507195-68309c04c4d0?auto=format,compress&fit=crop&q=80&w=800" 
                  alt="A warm candle glowing on a bedside table during twilight" 
                  loading="lazy"
                  decoding="async"
                  width="800"
                  height="1000"
                  className="w-full h-full object-cover opacity-90 grayscale-[0.3] hover:grayscale-0 transition-all duration-1000"
                />
              </picture>
            </div>
            <div className="absolute -bottom-10 -right-10 w-48 h-48 bg-ember/10 blur-[100px] pointer-events-none"></div>
          </div>
          <div className="order-1 lg:order-2">
            <span className="text-ember font-bold text-[10px] tracking-[0.4em] uppercase block mb-6">The Evening Ritual</span>
            <h2 className="font-serif text-4xl md:text-6xl text-linen mb-8 leading-tight">Designed for <span className="italic">Quiet Moments.</span></h2>
            <p className="text-linen/50 text-lg leading-relaxed mb-10 max-w-lg">
              Whether it's the final chapter of a book or a moment of reflection before sleep, our candles are crafted to provide a steady, calming glow that anchors your evening routine.
            </p>
            <div className="grid grid-cols-2 gap-8 border-t border-white/5 pt-10">
              <div>
                <span className="text-linen font-bold text-[10px] tracking-widest uppercase block mb-2">Burn Time</span>
                <p className="text-linen/40 text-sm italic font-serif">50+ Hours of Serenity</p>
              </div>
              <div>
                <span className="text-linen font-bold text-[10px] tracking-widest uppercase block mb-2">Vessel</span>
                <p className="text-linen/40 text-sm italic font-serif">Reusable Smoke Glass</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Minimal Story Teaser */}
      <section className="py-40 bg-smoke-light relative">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="mb-12 inline-block w-16 h-px bg-ember"
          />
          <h2 className="font-serif text-4xl md:text-6xl text-linen mb-10 leading-tight">Every light tells a <span className="italic opacity-80">story.</span></h2>
          <p className="text-linen/50 text-xl md:text-2xl font-serif italic max-w-2xl mx-auto leading-relaxed mb-16">
            "We believe scent is the invisible architecture of a space, capable of transforming the mundane into the magical."
          </p>
          <Link to="/about" className="px-12 py-5 border border-white/10 text-linen text-[11px] font-bold uppercase tracking-[0.3em] hover:bg-linen hover:text-smoke transition-all duration-500">
            Discover Our Roots
          </Link>
        </div>
      </section>
    </main>
  );
}